import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import api from '../lib/api';

// Types based on the API response
export interface MetricsPeriod {
  timestamps: string[];
}

export interface MetricData {
  metricName: string;
  namespace: string;
  unit: string;
  integrationId?: string;
  values: number[];
}

export interface MetricsResponse {
  data: {
    period: MetricsPeriod;
    metrics: MetricData[];
  };
}

export interface MetricsRequest {
  metric: {
    namespace: string;
    metricNames: string[];
  };
  period: {
    interval: number; // in seconds
    start: string; // ISO date string
    end: string; // ISO date string
    stat: 'sum' | 'avg' | 'min' | 'max';
  };
}

// Function to fetch metrics
export const fetchMetrics = async (
  request: MetricsRequest,
): Promise<MetricsResponse> => {
  const response = await api.post<MetricsResponse>(
    '/api/base/tenant/metrics',
    request,
  );
  return response.data;
};

// Generic hook to fetch metrics
export const useMetrics = (
  request: MetricsRequest | null,
  options?: { enabled?: boolean },
) => {
  return useQuery<MetricsResponse, Error>({
    queryKey: [
      'metrics',
      request?.metric.namespace,
      request?.metric.metricNames,
      request?.period.start,
      request?.period.end,
      request?.period.interval,
      request?.period.stat,
    ],
    queryFn: () => {
      if (!request) {
        throw new Error('Metrics request is required');
      }
      return fetchMetrics(request);
    },
    enabled: !!request && options?.enabled !== false,
  });
};
